import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo2,
  Redo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * TipTap rich text editor for "Comece por aqui" message.
 * Supports: bold, italic, underline, ordered/unordered lists, links,
 * paragraphs.
 *
 * Receives `value` (HTML string) and emits `onChange(html)`.
 */
export function TiptapEditor({ value, onChange, placeholder = "Digite aqui…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Use built-in heading/list configuration; disable horizontal_rule to keep editor compact.
        horizontalRule: false,
        codeBlock: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
          class: "text-midnight underline decoration-midnight/40 hover:text-ink",
        },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-slate prose-sm max-w-none min-h-[150px] p-3 focus:outline-none",
        "data-testid": "tiptap-editor-content",
      },
    },
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
  });

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL do link (deixe em branco para remover):", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const buttonClass = (active) =>
    `h-8 px-2 ${
      active
        ? "bg-midnight text-white hover:bg-ink hover:text-white"
        : "text-slate-700 hover:bg-slate-100"
    }`;

  return (
    <div className="border border-slate-300 rounded-md overflow-hidden bg-white">
      {/* Toolbar */}
      <div
        className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50"
        data-testid="tiptap-toolbar"
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(editor.isActive("bold"))}
          onClick={() => editor.chain().focus().toggleBold().run()}
          data-testid="tiptap-bold"
          title="Negrito"
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(editor.isActive("italic"))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          data-testid="tiptap-italic"
          title="Itálico"
        >
          <Italic className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(editor.isActive("underline"))}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          data-testid="tiptap-underline"
          title="Sublinhado"
        >
          <UnderlineIcon className="w-4 h-4" />
        </Button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(editor.isActive("bulletList"))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          data-testid="tiptap-bullet-list"
          title="Lista com marcadores"
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(editor.isActive("orderedList"))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          data-testid="tiptap-ordered-list"
          title="Lista numerada"
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(editor.isActive("link"))}
          onClick={setLink}
          data-testid="tiptap-link"
          title="Inserir / editar link"
        >
          <LinkIcon className="w-4 h-4" />
        </Button>
        <span className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(false)}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Desfazer"
        >
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={buttonClass(false)}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Refazer"
        >
          <Redo2 className="w-4 h-4" />
        </Button>
      </div>

      <EditorContent
        editor={editor}
        placeholder={placeholder}
        data-testid="tiptap-editor"
      />
    </div>
  );
}
